import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Tooltip,
  FormHelperText,
  FormControl,
  OutlinedInput,
  InputLabel,
  Icon,
  withStyles,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';

import ColorPicker from '../../../../widgets/ColorPicker';
import IconPicker from '../../../../widgets/IconPicker';

import styles from './styles';

export const DEBOUNCE_TIME = 1000;

const isNameValid = (name) => (
  name !== null
  && name !== undefined
  && name.length >= 2
  && name.length <= 50
);

export class StatusEditorComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      validation: {
        name: {
          isValid: true,
          message: '',
        },
      },
      updatedStatus: { },
    };
  }

  setUpdatedStatus = (changes) => {
    this.setState(({ updatedStatus }) => ({
      updatedStatus: {
        ...updatedStatus,
        ...changes,
      },
    }));
  }

  handleClose = () => {
    this.props.onClose();
  }

  handleArchive = () => {
    this.props.editStatus(this.props.status.id, { archived: true });
    this.handleClose();
  }

  handleUnarchive = () => {
    this.props.editStatus(this.props.status.id, { archived: false });
    this.handleClose();
  }

  handleNameChange = (e) => {
    const name = e.target.value;
    const isValid = isNameValid(name);
    this.setUpdatedStatus({ name });

    this.setState({
      validation: {
        name: {
          isValid,
          message: !isValid
            ? <span>Please provide a valid display name (between 2 and 50 characters in length).</span>
            : '',
        },
      },
    });
  }

  handleColorChange = (event) => {
    this.setUpdatedStatus({ iconColor: event.hex });
  }

  handleIconChange = (icon) => {
    this.setUpdatedStatus({ icon });
  }

  handleToggleEndingStatus = (event) => {
    const endingStatus = !this.props.status.endingStatus;
    if (this.props.status.endingStatus !== event.target.checked) {
      this.setUpdatedStatus({ endingStatus });
    }
    this.props.status.endingStatus = endingStatus;
  }

  handleToggleRequireComment = (event) => {
    const requireComment = !this.props.status.requireComment;
    if (this.props.status.requireComment !== event.target.checked) {
      this.setUpdatedStatus({ requireComment });
    }
    this.props.status.requireComment = requireComment;
  }

  handleMoveForward = () => {
    const currentStatusOrder = this.props.status.statusOrder;
    const newStatusOrder = this.props.nextStatus.statusOrder > currentStatusOrder

      ? this.props.nextStatus.statusOrder
      : this.props.nextStatus.statusOrder + 1;
    this.setUpdatedStatus({ statusOrder: newStatusOrder });
    this.setUpdatedStatus({
      id: this.props.nextStatus.id,
      statusOrder: currentStatusOrder,
    });
  }

  handleMoveBackward = () => {
    const currentStatusOrder = this.props.status.statusOrder;
    const newStatusOrder = this.props.previousStatus.statusOrder < currentStatusOrder

      ? this.props.previousStatus.statusOrder
      : this.props.previousStatus.statusOrder - 1;
    this.setUpdatedStatus({ statusOrder: newStatusOrder });
    this.setUpdatedStatus({
      id: this.props.previousStatus.id,
      statusOrder: currentStatusOrder,
    });
  }

  handleSave = () => {
    this.props.editStatus(this.props.status.id, this.state.updatedStatus);
    this.handleClose();
  }

  render() {
    const {
      classes,
      status: originalStatus,
    } = this.props;
    const {
      updatedStatus,
      validation,
    } = this.state;

    const status = {
      ...originalStatus,
      ...updatedStatus,
    };

    return (
      <Dialog
        open
        onClose={this.handleClose}
        classes={{ paper: classes.dialog }}
      >
        {
          !status.id
            ? <DialogTitle>Add New Status</DialogTitle>
            : <DialogTitle>Edit Status</DialogTitle>
        }
        <DialogContent>
          <FormControl
            fullWidth
            error={!this.state.validation.name.isValid}
            variant="outlined"
          >
            <InputLabel htmlFor="tenant-display-name">Name</InputLabel>
            <OutlinedInput
              id={'status-name'.concat(status.id)}
              value={status.name}
              onChange={this.handleNameChange}
              labelWidth={45}
            />
            <FormHelperText>
              {
                !this.state.validation.name.isValid
                && this.state.validation.name.message
              }
            </FormHelperText>
          </FormControl>
          <ColorPicker color={status.iconColor} onColorChange={this.handleColorChange} />
          <IconPicker icon={status.icon} onIconChange={this.handleIconChange} />

          <div className={classes.toggleFieldContainer}>
            <div className={classes.toggleFieldLabelContainer}>
              <Typography
                variant="subtitle1"
                className={classes.toggleFieldLabel}
              >
                Autocomplete
              </Typography>
              <Tooltip title="Events will automatically be completed when it is moved to this status" id={`status-autocomplete-toggle-${status.id}`}>
                <Icon fontSize="small" color="disabled">info_outline</Icon>
              </Tooltip>
            </div>
            <Checkbox
              checked={status.endingStatus}
              onChange={this.handleToggleEndingStatus}
              value="endingStatus"
              color="primary"
            />
          </div>

          <div className={classes.toggleFieldContainer}>
            <div className={classes.toggleFieldLabelContainer}>
              <Typography
                variant="subtitle1"
                className={classes.toggleFieldLabel}
              >
                Require Comment
              </Typography>
              <Tooltip title="Any event update to this status will require the user to provide a comment" id={`require-comment-toggle-${status.id}`}>
                <Icon fontSize="small" color="disabled">info_outline</Icon>
              </Tooltip>
            </div>
            <Checkbox
              checked={status.requireComment}
              onChange={this.handleToggleRequireComment}
              value="requireComment"
              color="primary"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose}>
            Cancel
          </Button>
          {
            !!status.id
              && !status.archived
              && (
                <Button onClick={this.handleArchive} color="inherit" className={classes.archiveButton}>
                  Archive
                </Button>
              )
          }
          {
            !!status.id
              && !!status.archived
              && (
                <Button onClick={this.handleUnarchive} color="secondary">
                  Restore
                </Button>
              )
          }
          <Button onClick={this.handleSave} color="primary" variant="contained" disabled={!validation.name.isValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

StatusEditorComponent.propTypes = {
  status: PropTypes.object,
  nextStatus: PropTypes.object,
  previousStatus: PropTypes.object,
  editStatus: PropTypes.func.isRequired,
  classes: PropTypes.object,
  onClose: PropTypes.func,
};

StatusEditorComponent.defaultProps = {
  classes: {},
  nextStatus: undefined,
  previousStatus: undefined,
  status: { name: '' },
  onClose: () => {},
};

export default withStyles(styles)(StatusEditorComponent);
